/**
 * PackContext — the app-shell's single source of truth for "which packs
 * is the current tenant on, and which view mode applies?".
 *
 * Reuses the EXISTING instance-resolution mechanism, no parallel path:
 *   - useTenantInstance() supplies selectedInstanceId (the same Redux-
 *     backed reader Header/Settings use). The session gate
 *     (useSessionInit) guarantees an instance is resolved before this
 *     provider ever mounts, so `skip` is defensive, not a real state.
 *   - useGetInstalledPacksQuery passes instanceId explicitly so it
 *     lands in the RTK Query cache key → a no-reload (General Settings)
 *     instance switch produces a fresh fetch instead of serving the
 *     previous tenant's packs. The Header switcher full-page-reloads,
 *     remounting this provider clean.
 *
 * MODE — two fields, deliberately:
 *   - derivedMode  : the tenant default, computed purely from installed
 *                    packs. 'operator_crm' if ANY installed pack
 *                    declares it, else 'lead_gen_only'. Zero installed
 *                    packs → 'lead_gen_only' so the 5 provisioned Sky
 *                    Advisor tenants keep the existing Advisor UI
 *                    unchanged.
 *   - currentMode  : what the user is actively viewing.
 *
 * v1 TOGGLE (this build, not deferred): when a tenant has BOTH a
 * lead_gen_only pack (advisor) AND an operator_crm pack (senior_living)
 * installed, the user can toggle currentMode between the two. The
 * choice persists in localStorage under 'sky-current-mode' — same
 * client-persistence pattern as 'sky-selected-instance-id'. There is
 * NO server-side user preference yet; that is the post-demo layer.
 * localStorage is sufficient for the demo (survives reloads, including
 * the Header account-switcher's full-page reload).
 *
 *   - canToggleMode : true only when both modes are achievable from the
 *                     installed packs. Single-pack tenants get no toggle
 *                     (single pack = single view).
 *   - currentMode   : persisted choice IF it is still achievable for the
 *                     currently installed packs, else derivedMode. The
 *                     validity clamp matters on the no-reload instance
 *                     switch: persisted 'operator_crm' must fall back to
 *                     'lead_gen_only' when the new tenant is advisor-only.
 *
 * v2 (post-demo): replace the localStorage read/write with a server-
 * side per-user preference. The contract here does not change — only
 * the persistence backing does. Do not collapse derivedMode and
 * currentMode into one field.
 */

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useTenantInstance } from "../../hooks/useTenantInstance";
import {
  useGetInstalledPacksQuery,
  useGetPackFieldsQuery,
  useGetTenantTerminologyQuery,
  type InstalledPack,
  type PackFieldsByEntity,
  type PackMode,
  type WireLeadProfile,
} from "../../store/api/packsApi";
import { resolveTerminology, type Terminology } from "../../../lib/packs/terminology";

const MODE_STORAGE_KEY = "sky-current-mode";

function isPackMode(v: unknown): v is PackMode {
  return v === "operator_crm" || v === "lead_gen_only";
}

function readStoredMode(): PackMode | null {
  try {
    const v = localStorage.getItem(MODE_STORAGE_KEY);
    return isPackMode(v) ? v : null;
  } catch {
    // private-browsing / disabled storage — behave as "no preference"
    return null;
  }
}

function writeStoredMode(mode: PackMode): void {
  try {
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    // Best-effort; toggle still works in-session via React state.
  }
}

interface PackContextType {
  /** Fully-specified installed packs (server filters unknown pack_ids). */
  packs: InstalledPack[];
  /**
   * True on first load AND during a no-reload cross-instance refetch
   * (Anchor 2: Sidebar shows a loader, never stale, during the
   * General Settings instance switch). False when there is no instance
   * to query — the session gate handles that upstream.
   */
  isLoading: boolean;
  /**
   * True while the installed packs for the CURRENT instance have not resolved
   * yet (first load, or a cross-instance switch) — i.e. the mode is unknown and
   * `currentMode` is only the 'lead_gen_only' default. Unlike `isLoading` it
   * stays false during a same-instance background refetch, so a surface that
   * blanks on it (the sidebar) does not re-flash on every refetch. False after
   * an error (the Advisor default then stands).
   *
   * Optional only so hand-built test providers need not name it; the real
   * provider always sets it, and absent reads as false.
   */
  isResolvingMode?: boolean;
  /** Is a pack with this id installed for the current tenant? */
  hasPack: (packId: string) => boolean;
  /**
   * Does ANY installed pack declare this in-page feature capability
   * (Pack.features — E2 vertex gate)? Gates detail tabs, wizard blocks,
   * list filters, and report surfaces that are pack-optional. Use this,
   * not hasPack(id), so a second vertical can enable the same feature
   * without touching the gate.
   */
  hasFeature: (featureId: string) => boolean;
  /**
   * The tenant's ACTIVE `profile_v1` lead profile (vertical-lead-profile §3.4), or
   * null. Null for every tenant on today's behaviour: no pack, advisor only,
   * behavioral health, and senior living (whose `legacy_senior_living` label
   * dispatches to the untouched code). A surface asks this ONE question and
   * renders exactly as before when the answer is null.
   *
   * Optional only so the many hand-built test providers need not name it; the real
   * provider always sets it, and absent reads exactly as null.
   */
  leadProfile?: Extract<WireLeadProfile, { engine: "profile_v1" }> | null;
  /**
   * True when ANY installed pack declares mode 'operator_crm'.
   *
   * Phase-0 de-hardcoding: app-shell consumers (route guard, sidebar,
   * landing gate, CRM-mode chrome) used to ask hasPack("senior_living"),
   * baking the one vertical into every gate. What they all actually
   * mean is "is the CRM shell available for this tenant" — which any
   * operator-mode pack (senior_living today, behavioral_health tomorrow)
   * satisfies. Use hasPack(id) only for genuinely pack-SPECIFIC
   * features, not for CRM-shell gating.
   */
  hasOperatorPack: boolean;
  /**
   * Effective entity-noun vocabulary (Phase-1 terminology service):
   * PLATFORM_TERMS overlaid with each installed pack's overrides in
   * install order. Consume via useTerm() (src/hooks/useTerm.ts), which
   * also handles the no-provider fallback for tests.
   */
  terms: Terminology;
  /**
   * Resolved custom-field DEFINITIONS per entity (Phase-1 runtime): base
   * platform fields + installed packs, projected display-safe. Consume via
   * usePackCustomFields(entity) (mirrors useTerm's no-provider tolerance).
   * VALUES never live here — a person's custom_data comes from its detail read.
   */
  customFields: PackFieldsByEntity;
  /** Tenant default view mode, derived from installed packs. */
  derivedMode: PackMode;
  /** Active view mode. === derivedMode unless the user toggled (v1) / a user-pref overrides (v2). */
  currentMode: PackMode;
  /** True only when both modes are achievable → render the toggle. */
  canToggleMode: boolean;
  /** Toggle/set the active view mode. No-op when !canToggleMode. */
  setCurrentMode: (mode: PackMode) => void;
}

export const PackContext = createContext<PackContextType | null>(null);

export function PackProvider({ children }: { children: React.ReactNode }) {
  const { selectedInstanceId } = useTenantInstance();
  const skip = !selectedInstanceId;

  const {
    data,
    currentData,
    isLoading: queryIsLoading,
    isFetching,
  } = useGetInstalledPacksQuery({ instanceId: selectedInstanceId }, { skip });

  // Tenant terminology layer (Phase-1): per-tenant overrides on top of
  // pack overrides — two customers on the SAME vertical can run
  // different vocabularies. Non-blocking: nav/pages render with
  // pack/platform terms until this resolves; a failed read degrades to
  // the pack layer rather than blocking the shell.
  const { data: tenantTerms } = useGetTenantTerminologyQuery(
    { instanceId: selectedInstanceId },
    { skip },
  );

  // Custom-field definitions (Phase-1 runtime): resolved server-side from the
  // installed packs. Non-blocking, same as terminology — pages render without
  // custom fields until this resolves.
  const { data: packFields } = useGetPackFieldsQuery({ instanceId: selectedInstanceId }, { skip });

  // The user's explicit toggle choice. Seeded from localStorage; null
  // means "no explicit choice → follow derivedMode".
  const [storedMode, setStoredMode] = useState<PackMode | null>(() => readStoredMode());

  const setCurrentMode = useCallback((mode: PackMode) => {
    writeStoredMode(mode);
    setStoredMode(mode);
  }, []);

  const value = useMemo<PackContextType>(() => {
    const packs = data ?? [];
    // No instance to query → not "loading forever"; the session gate
    // resolves an instance before this provider mounts, so this is a
    // defensive branch only.
    const isLoading = !skip && (queryIsLoading || isFetching);
    // `currentData` is undefined until THIS instance's packs arrive (it does not
    // carry the previous instance's packs across a switch, unlike `data`).
    const isResolvingMode = !skip && isFetching && currentData === undefined;

    const availableModes = new Set<PackMode>(packs.map((p) => p.mode));
    const hasOperatorPack = availableModes.has("operator_crm");
    const derivedMode: PackMode = hasOperatorPack ? "operator_crm" : "lead_gen_only";

    // Toggle is meaningful only when BOTH views are achievable.
    const canToggleMode = availableModes.has("operator_crm") && availableModes.has("lead_gen_only");

    // Honor the persisted choice only if it is still achievable for the
    // currently installed packs (validity clamp — see header).
    const currentMode: PackMode =
      storedMode && availableModes.has(storedMode) ? storedMode : derivedMode;

    const installedIds = new Set(packs.map((p) => p.packId));
    const hasPack = (packId: string) => installedIds.has(packId);

    // Union of every installed pack's feature capabilities (E2 vertex
    // gate). Feature-oriented on purpose — see interface note.
    const featureIds = new Set<string>(packs.flatMap((p) => p.features ?? []));
    const hasFeature = (featureId: string) => featureIds.has(featureId);

    // Terminology: platform defaults → pack overrides in install order
    // (the server returns rows ORDER BY installed_at) → tenant overrides
    // (skycrm_terminology) as the final layer.
    const terms = resolveTerminology([
      ...packs.map((p) => p.terminology ?? {}),
      tenantTerms?.overrides ?? {},
    ]);

    const customFields = packFields?.fields ?? {};

    // At most one installed pack declares a profile (D-3, enforced at install);
    // only a profile_v1 one changes any surface.
    const declared = packs.flatMap((p) => (p.leadProfile ? [p.leadProfile] : []));
    const leadProfile =
      declared.length === 1 && declared[0].engine === "profile_v1" ? declared[0] : null;

    return {
      packs,
      isLoading,
      isResolvingMode,
      hasPack,
      hasFeature,
      leadProfile,
      hasOperatorPack,
      terms,
      customFields,
      derivedMode,
      currentMode,
      canToggleMode,
      setCurrentMode,
    };
  }, [
    data,
    currentData,
    tenantTerms,
    packFields,
    skip,
    queryIsLoading,
    isFetching,
    storedMode,
    setCurrentMode,
  ]);

  return <PackContext.Provider value={value}>{children}</PackContext.Provider>;
}

export function usePacks(): PackContextType {
  const context = useContext(PackContext);
  if (!context) {
    throw new Error("usePacks must be used within PackProvider");
  }
  return context;
}
